/**
 * SUPERVISOR-UNMATCHED-QUESTIONS-MODAL
 * Opened from the benchmark card in the template editor. Lists every scale question of the
 * template, split into 3 tabs: "avec benchmark" / "sans benchmark" / "non concernées" (choice/
 * open, no benchmark notion at all).
 * - A normal client (or a CS connected as that client) only gets a read-only list plus sector
 *   management (canManageSector) — no association is exposed to them, the question bank is a
 *   pool shared across every account.
 * - A supervisor (canAssociate — raw or connected as a client) can additionally search the
 *   question bank (archive.bank_questions, KB features/bank-questions-vision.md) for a likely
 *   match for an unmatched question and associate it, or create a brand new bank entry when
 *   nothing matches. Editor-only
 *   routes gated by supervisorConnectedId when connected as a client — that session never
 *   carries the /supervisor/* JWT the raw-supervisor routes require. Kept self-contained here
 *   rather than linking out to a separate screen, which requires re-entering through the normal
 *   supervisor login/account-selection flow to work.
 *
 * Note (2026-09-10, updated 2026-09-11): associating/creating here enriches bank_questions.texts[]
 * for later use, but does NOT retroactively flip a question's "avec/sans benchmark" status in this
 * same modal within the same render — getTemplateBenchmarkStatus() is now bank_id-based (KB §21),
 * so a newly-associated text WILL show up correctly on the next fetch, just not instantly without
 * one (see TemplateBenchmarkSidebar.tsx's isEdited-triggered refresh, KB §14).
 */
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { toast } from "react-toastify"
import { faMagnifyingGlass, faLink, faArrowRight, faPlus, faBuilding } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useAppDispatch, useAppSelector } from "@/core/root-reducer"
import Modal from "@/components/modal"
import Card from "@/components/card"
import Link from "@/components/link"
import Chip from "@/components/chip"
import ListFields from "@/components/list-fields"
import ListItem from "@/components/list-item"
import ListCell from "@/components/list-cell"
import ListButton from "@/components/list-button"
import SectorGrid, { SECTOR_ICONS } from "./SectorGrid"
import BankQuestionTagChips from "@/features/benchmark/entities/question/ui/BankQuestionTagChips"
import { Axis } from "@/redux/axis.types"
import { Topic } from "@/redux/topic.types"
import { topicActivate } from "@/redux/topic.actions"
import {
  suggestBankQuestions,
  linkTextToBankQuestion,
  linkTextToBankQuestion_AsSupervisor,
  createBankQuestion,
  createBankQuestion_AsSupervisor,
  calculateOneBankQuestion_AsSupervisor,
  BankQuestionSuggestion
} from "@/features/benchmark/entities/question/model/bankQuestion.thunk"

interface SupervisorQuestion {
  questionId: string
  text: string
  axisId: string | null
  topicId: string
  bankId: string | null
  hasBenchmark: boolean
}

interface NoBenchmarkQuestion {
  questionId: string
  text: string
  axisId: string | null
  topicId: string
}

interface Props {
  questions: SupervisorQuestion[]
  //Choice/open questions — no benchmark notion at all, informational only, never actionable
  noBenchmarkQuestions?: NoBenchmarkQuestion[]
  axisList: Axis[]
  topicList: Topic[]
  //Raw supervisor (editing a template directly from /supervisor/templates, no client
  //impersonation) has no app JWT at all — goes through /supervisor/bank-questions/* instead
  asRawSupervisor?: boolean
  //Search/link actions — supervisor-only (raw or connected as a client)
  canAssociate?: boolean
  //Sector set/change/reset — normal client or a CS connected as that client
  canManageSector?: boolean
  sector?: string | null
  onSelectSector?: (sector: string | null) => void
  onClose: () => void
  onLinked: () => void //Refetch the template status after a successful link/create
}

const TAB_UNMATCHED = "unmatched"
const TAB_MATCHED = "matched"
const TAB_NO_NOTION = "noNotion"

function SupervisorUnmatchedQuestionsModal(props: Props) {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const _session = useAppSelector((state) => state._session)
  //Default to the first non-empty tab (sans benchmark > avec benchmark > non concernées) rather
  //than always opening on "sans benchmark" when it happens to be empty
  const [activeTab, setActiveTab] = useState(() => {
    if (props.questions.some((q) => !q.hasBenchmark)) return TAB_UNMATCHED
    if (props.questions.some((q) => q.hasBenchmark)) return TAB_MATCHED
    return TAB_NO_NOTION
  })
  const [openFor, setOpenFor] = useState<string | null>(null)
  const [suggestionsById, setSuggestionsById] = useState<Record<string, BankQuestionSuggestion[]>>({})
  //Only the top match (suggestBankQuestions sorts by similarity desc) is shown by default —
  //"afficher plus" reveals the rest. Single flag rather than per-question: only one suggest
  //panel is ever open at a time (openFor), reset whenever a new one opens.
  const [showAllSuggestions, setShowAllSuggestions] = useState(false)
  //Sector picker is embedded here rather than a second stacked Modal — never two modals at once
  const [pickingSector, setPickingSector] = useState(false)

  function handleSelectSector(sector: string | null) {
    setPickingSector(false)
    props.onSelectSector?.(sector)
  }

  //Same order as displayed in the editor's nav : axis order, then topic order within that axis.
  //Questions without an axis (findIndex -1) go last, not first.
  function axisOrder(axisId: string | null) {
    const idx = props.axisList.findIndex((ax) => ax.id === axisId)
    return idx === -1 ? props.axisList.length : idx
  }

  function sortByTemplateOrder<T extends { axisId: string | null; topicId: string }>(list: T[]): T[] {
    return [...list].sort((a, b) => {
      const axisDiff = axisOrder(a.axisId) - axisOrder(b.axisId)
      if (axisDiff !== 0) return axisDiff
      return props.topicList.findIndex((t) => t.id === a.topicId) - props.topicList.findIndex((t) => t.id === b.topicId)
    })
  }

  const unmatchedQuestions = sortByTemplateOrder(props.questions.filter((q) => !q.hasBenchmark))
  const linkedQuestions = sortByTemplateOrder(props.questions.filter((q) => q.hasBenchmark))
  const noBenchmarkQuestions = sortByTemplateOrder(props.noBenchmarkQuestions || [])

  function getAxisColor(axisId: string | null) {
    return (axisId && props.axisList.find((a) => a.id === axisId)?.color) || "#8c8c8c"
  }

  async function handleSuggest(questionId: string, text: string) {
    if (openFor === questionId) {
      setOpenFor(null)
      return
    }
    setOpenFor(questionId)
    setShowAllSuggestions(false)
    if (!suggestionsById[questionId]) {
      const res = await dispatch(suggestBankQuestions({ text, asSupervisor: props.asRawSupervisor })).unwrap()
      setSuggestionsById((prev) => ({ ...prev, [questionId]: res.suggestions }))
    }
  }

  async function handleAssociate(questionId: string, text: string, suggestion: BankQuestionSuggestion) {
    const res = await dispatch(
      props.asRawSupervisor
        ? linkTextToBankQuestion_AsSupervisor({ bankId: suggestion.bankId, text })
        : linkTextToBankQuestion({ bankId: suggestion.bankId, text })
    ).unwrap()

    if (res.error) return // fetchBackend already toasted the real error

    if (res.conflictBankId) {
      // This text already belongs to a different bank_questions row — linking it here too would
      // silently fragment one real question across two rows (KB features/bank-questions-vision.md
      // §29). Refused server-side; nothing changed, so the panel stays open as-is.
      toast(t("benchmark_editor_supervisor_link_conflict_toast"), { type: "error" })
      return
    }

    toast(t(res.added === 0 ? "benchmark_editor_supervisor_already_linked_toast" : "benchmark_editor_supervisor_associated_toast"))
    setOpenFor(null)
    // Same as BankQuestionManageModal's own "Associer" (2026-09-18, Tom — keep the two consistent)
    // — linking a text never recomputes anything on its own, syncBankIdFromTexts only runs as
    // part of a calculate. Raw-supervisor only: connected-as-a-client here never carries the
    // /supervisor/* JWT this route requires (see this file's own doc comment above).
    if (props.asRawSupervisor) await dispatch(calculateOneBankQuestion_AsSupervisor(suggestion.bankId)).unwrap()
    props.onLinked()
  }

  async function handleCreate(questionId: string, text: string) {
    const res = props.asRawSupervisor
      ? await dispatch(createBankQuestion_AsSupervisor({ text })).unwrap()
      : await dispatch(createBankQuestion(text)).unwrap()

    if (res.error || !res.id) return // fetchBackend already toasted the real error

    toast(t(res.alreadyExists ? "benchmark_editor_supervisor_already_in_bank_toast" : "benchmark_editor_supervisor_created_toast"))
    setOpenFor(null)
    if (props.asRawSupervisor) await dispatch(calculateOneBankQuestion_AsSupervisor(res.id)).unwrap()
    props.onLinked()
  }

  //Clicking a question jumps to it in the editor's topic list (nav-topic-<id>, see
  //editor-nav.widget.tsx) without closing this modal — separate from the search/link actions,
  //which stay on their own buttons (Tom, 2026-09-14).
  function focusQuestionInEditor(topicId: string) {
    const topic = props.topicList.find((t) => t.id === topicId)
    if (!topic) return

    dispatch(topicActivate(topic))
    setTimeout(() => {
      document.querySelector(`#nav-topic-${topicId}`)?.scrollIntoView({ behavior: "smooth", block: "center" })
    }, 200)
  }

  //Choice/open questions — no notion of benchmark, purely informational, never actionable
  function renderNoBenchmarkRow(q: NoBenchmarkQuestion) {
    return (
      <div key={q.questionId} style={{ borderLeft: `3px solid ${getAxisColor(q.axisId)}` }}>
        <ListItem isSmall className="items-center">
          <ListCell isFlex text={q.text} className="grey-t" onClick={() => focusQuestionInEditor(q.topicId)} />
        </ListItem>
      </div>
    )
  }

  function renderRow(q: SupervisorQuestion) {
    if (!props.canAssociate) {
      //Read-only for a normal client (or a CS connected as that client) — no search/link/unlink
      return (
        <div key={q.questionId} style={{ borderLeft: `3px solid ${getAxisColor(q.axisId)}` }}>
          <ListItem isSmall className="items-center">
            <ListCell
              isFlex
              text={q.text}
              className="grey-t"
              onClick={() => focusQuestionInEditor(q.topicId)}
            />
          </ListItem>
        </div>
      )
    }

    const isSuggestOpen = openFor === q.questionId

    return (
      <div key={q.questionId} style={{ borderLeft: `3px solid ${getAxisColor(q.axisId)}` }}>
        <ListItem isSmall className="items-center">
          <ListCell isFlex>
            <span
              className="_hover grey-t"
              onClick={() => focusQuestionInEditor(q.topicId)}>
              {q.text}
            </span>
          </ListCell>
          {/* No action left once a question is linked to the catalog (q.hasBenchmark, see KB
              §34) — search/associate and "voir les liens" were both only useful to resolve an
              unmatched question, nothing left to do here (Tom, 2026-09-14). */}
          {!q.hasBenchmark && (
            <ListCell width={96} className="items-center justify-end gap-1">
              {/* Direct create next to the search icon (2026-09-18) — "Créer" already existed as
                  the first row inside the search panel (handleCreate below), but opening that
                  panel just to reach it was an extra click every time for a question you already
                  know isn't in the catalog yet. */}
              <ListButton
                icon={faPlus}
                text={t("benchmark_editor_supervisor_create")}
                onClick={() => handleCreate(q.questionId, q.text)}
              />
              <ListButton
                icon={faMagnifyingGlass}
                text={t("benchmark_editor_supervisor_search")}
                onClick={() => handleSuggest(q.questionId, q.text)}
              />
            </ListCell>
          )}
        </ListItem>

        {isSuggestOpen && (suggestionsById[q.questionId] || []).length === 0 && (
          <div className="grey-t text-sm" style={{ padding: "4px 0 8px 12px" }}>
            {t("benchmark_editor_supervisor_no_suggestion")}
          </div>
        )}

        {isSuggestOpen && (
          <div style={{ marginLeft: 12, marginBottom: 8 }}>
            <Card isWithoutPadding className="mt-0">
              <ListItem isSmall isActive className="items-center">
                <ListCell isFlex>
                  <span
                    className="_hover"
                    style={{ fontWeight: 700 }}
                    onClick={() => handleCreate(q.questionId, q.text)}>
                    <FontAwesomeIcon icon={faPlus} style={{ marginRight: 6 }} />
                    {t("benchmark_editor_supervisor_create")}
                  </span>
                </ListCell>
              </ListItem>

              {(suggestionsById[q.questionId] || []).length > 0 && (
                <>
                  {/* list-container-fields has its own 18px top padding baked into shared CSS —
                      pulled up here so it doesn't stack with the "create" row's own bottom
                      padding into an oversized gap between the two. */}
                  <div style={{ marginTop: -16 }}>
                    <ListFields>
                      <ListCell isFlex text={t("benchmark_editor_supervisor_suggestion_col")} />
                      <ListCell width={80} text={t("benchmark_editor_supervisor_score_col")} />
                      <ListCell width={32} />
                    </ListFields>
                  </div>

                  {/* Capped at 8 results (suggestBankQuestions limit, sorted by similarity desc)
                      but only the top match is shown by default — "afficher plus" reveals the
                      rest, scrolling locally. suggestBankQuestions() already excludes any row
                      this text can't actually be linked to (its own current home included, per
                      its alreadyLinkedTo field) — shows up as an empty list here rather than a
                      distinct message (Tom, 2026-09-14, KB features/bank-questions-vision.md
                      §29/§34). */}
                  <div style={{ maxHeight: showAllSuggestions ? 240 : undefined, overflowY: showAllSuggestions ? "auto" : undefined }}>
                    {(showAllSuggestions ? suggestionsById[q.questionId] : suggestionsById[q.questionId].slice(0, 1)).map((sug) => (
                      <ListItem key={sug.bankId} isSmall className="items-center">
                        <ListCell isFlex>
                          <div className="flex items-center gap-1 flex-wrap">
                            <span>{sug.question}</span>
                            {sug.tags.length > 0 && (
                              <span className="grey-t text-sm">— {sug.tags.join(", ")}</span>
                            )}
                            {sug.responseCount > 0 && (
                              <span className="grey-t text-sm">
                                {t("benchmark_editor_supervisor_response_count", { count: sug.responseCount })}
                              </span>
                            )}
                            {/* Reverse: at least one archived instance already linked to this
                                catalog row is reverse (2026-09-21). */}
                            <BankQuestionTagChips isReverse={sug.isReverse} scalePoints={sug.scalePoints} />
                          </div>
                        </ListCell>
                        <ListCell width={80} text={`${Math.round(sug.similarity * 100)}%`} />
                        <ListCell width={32} className="items-center">
                          <ListButton
                            icon={faLink}
                            text={t("benchmark_editor_supervisor_associate")}
                            onClick={() => handleAssociate(q.questionId, q.text, sug)}
                          />
                        </ListCell>
                      </ListItem>
                    ))}
                  </div>

                  {!showAllSuggestions && suggestionsById[q.questionId].length > 1 && (
                    <ListItem isSmall className="items-center">
                      <ListCell isFlex>
                        <Link isWithoutMargin onClick={() => setShowAllSuggestions(true)}>
                          {t("benchmark_editor_supervisor_show_more", { count: suggestionsById[q.questionId].length - 1 })}
                        </Link>
                      </ListCell>
                    </ListItem>
                  )}
                </>
              )}
            </Card>
          </div>
        )}
      </div>
    )
  }

  //Reuse the same keys as the card's own legend (benchmark_editor_with_benchmark etc.) rather
  //than the old benchmark_editor_supervisor_unmatched_heading/linked_heading — removed as dead
  //keys in an earlier pass, when these sections were still stacked instead of tabbed. Capitalized
  //here rather than in the shared translation string, which is also used lowercase mid-sentence
  //(prefixed by a count) in the sidebar card's own legend.
  function capitalize(text: string) {
    return text.charAt(0).toUpperCase() + text.slice(1)
  }

  const tabChips = [
    { id: TAB_UNMATCHED, label: capitalize(t("benchmark_editor_without_benchmark")), count: unmatchedQuestions.length },
    { id: TAB_MATCHED, label: capitalize(t("benchmark_editor_with_benchmark")), count: linkedQuestions.length },
    { id: TAB_NO_NOTION, label: capitalize(t("benchmark_editor_no_notion")), count: noBenchmarkQuestions.length }
  ]

  return (
    <Modal
      onClose={props.onClose}
      isCloseButtonVisible
      isLarge
      className="bank-questions-modal"
      title={t("benchmark_editor_panel_title")}>
      {pickingSector ? (
        <>
          <div className="flex items-center justify-between" style={{ paddingBottom: 12 }}>
            <b>{t("benchmark_editor_sector_modal_title")}</b>
            {props.sector && (
              <Link isWithoutMargin onClick={() => handleSelectSector(null)}>
                {t("benchmark_editor_sector_reset")}
              </Link>
            )}
          </div>
          <SectorGrid current={props.sector} onSelect={handleSelectSector} />
        </>
      ) : (
        <>
          {props.canManageSector && (
            <div className="flex items-center justify-between flex-wrap grey-t" style={{ padding: "0 0 20px" }}>
              <div className="flex items-center gap-1 flex-wrap">
                <b>{t("benchmark_editor_sector_label")}</b> :
                {props.sector ? (
                  <Link isWithoutMargin onClick={() => setPickingSector(true)}>
                    {/* Hardcoded faBuilding regardless of the actual sector (found live,
                        2026-09-18) — SECTOR_ICONS already has one per sector, same set SectorGrid
                        itself uses one line below for the picker. */}
                    <FontAwesomeIcon icon={SECTOR_ICONS[props.sector] || faBuilding} style={{ marginRight: 4 }} />
                    {t("benchmark_sector_" + props.sector)}
                  </Link>
                ) : (
                  <Link isWithoutMargin icon={faArrowRight} onClick={() => setPickingSector(true)}>
                    {t("benchmark_editor_sector_define")}
                  </Link>
                )}
              </div>

            </div>
          )}

          <div className="flex items-center gap-2 flex-wrap" style={{ position: "sticky", top: 0, zIndex: 1, background: "#fff", paddingBottom: 20 }}>
            {tabChips.map((tab) => (
              <Chip
                key={tab.id}
                isWithMargin
                onClick={() => setActiveTab(tab.id)}
                color={activeTab === tab.id ? _session.accountColors.active : null}>
                {tab.label} ({tab.count})
              </Chip>
            ))}
          </div>

          <div style={{ marginTop: 12, minHeight: 320 }}>
            {activeTab === TAB_UNMATCHED &&
              (unmatchedQuestions.length > 0 ? (
                unmatchedQuestions.map(renderRow)
              ) : (
                <div className="grey-t text-sm" style={{ padding: "12px 0" }}>
                  {t("utils_no_results")}
                </div>
              ))}
            {activeTab === TAB_MATCHED &&
              (linkedQuestions.length > 0 ? (
                linkedQuestions.map(renderRow)
              ) : (
                <div className="grey-t text-sm" style={{ padding: "12px 0" }}>
                  {t("utils_no_results")}
                </div>
              ))}
            {activeTab === TAB_NO_NOTION &&
              (noBenchmarkQuestions.length > 0 ? (
                noBenchmarkQuestions.map(renderNoBenchmarkRow)
              ) : (
                <div className="grey-t text-sm" style={{ padding: "12px 0" }}>
                  {t("utils_no_results")}
                </div>
              ))}
          </div>
        </>
      )}
    </Modal>
  )
}

export default SupervisorUnmatchedQuestionsModal
